import React, { useEffect, useLayoutEffect, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Tabs,
  Tab,
  Typography,
  Select,
  styled,
  Grid2,
} from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  DependencyOperator,
  FbeDependency,
  FbeForm,
  FbeFrontline,
  FbeNumberMultiAdjustmentDirectionType,
  FbeNumberMultiAdjustmentImpactType,
  FbeOption,
  FbeQuestion,
  FbeQuestionAnswer,
  FbeQuestionAnswerAction,
  FbeQuestionFeature,
  fbeQuestionFields,
  FbeQuestionType,
  FbeReferenceType,
  FbeRowPositionName,
  FbeSection,
  FbeType,
  FbeVariableTranslations,
  FbeVariableType,
  getLanguageDisplayName,
  LocalisedText,
  LogicalOperator,
} from '../../services/fbeTypes';
import { ReferenceImage } from '../ReferenceImage';
import { useImageContext } from '../../imageContext';
import InputField from '../InputField';
import InputRowContainer from '../InputRowContainer';
import MultiSelect from '../MultiSelect';
import { answerActionOptions, externalTypeOptions, generateExternalDataToOptions, generatInternalVariableToOptions, logicalOperatorOptions, multiAdjustmentDirectionOptions, multiAdjustmentImpactOptions, operatorOptions, questionFeatureOptions, questionLoopRepeatOptions, questionTypeOptions, tableTypeOptions } from '../../services/options';
import SingleSelect from '../SingleSelect';
import LineAction, { LineActionType } from '../LineAction';
import FBCheckbox from '../FBCheckbox';
import LineSeparator from '../LineSeparator';
import ConditionalWrapper from '../ConditionalWrapper';
import FBRadio from '../FBRadio';
import MultiSelectFree from '../MultiSelectFree';
import EditContainer from '../EditContainer';
import { SmallTabBoxStyleStyled, SmallTabStyleStyled } from '../EditForm';
import { deepEqual, hasQuestionChanges, isQuestionValid, isSectionValid } from '../../services/service';
// import InputFieldResize from '../InputFieldResize';
import FieldLock from '../FieldLock';
import RichTextField from '../RichTextField';
interface EditQuestionFormProps {
  readOnly: boolean;
  formSections: FbeSection[];
  form: FbeForm | undefined;
  question: any;
  originalQuestion: any;
  onUpdateQuestion: any;
  imageList: string[];
  sectionIndex: number;
  onSubmitAllSections: () => Promise<void>;
  language: string;
  supportedLanguages: string[];
  frontline?: FbeFrontline;
}

const GapStyled = styled(Box)({
  gap: '16px',
});

const ButtonStyled = styled(Button)({
  borderRadius: '24px',
  padding: '6px 50px',
});

export default function EditQuestion(
  props: EditQuestionFormProps
): JSX.Element {
  const [tabIndex, setTabIndex] = useState(0);
  const [smallTabIndex, setSmallTabIndex] = useState(0);
  const history = useNavigate();
  const [formQuestionState, setFormQuestionState] = useState<FbeQuestion>(
    props.question
  );
  const [changesMade, setChangesMade] = useState(false);
  const [formQuestionValid, setFormQuestionValid] = useState(false);
  const [touchedFields, setTouchedFields] = useState<{
    [key: string]: boolean;
  }>({});

  const {
    setQuestionImage,
    setSelected,
    setItemList,
    setTimestamp,
    setCurrentElement,
} = useImageContext();

  setItemList(props.imageList);

  const generateLabelOptions = (options: string[]) : FbeOption[] => {
    const optionLabels: string[] = [...options];

    props.formSections.map(section => {
      section.questions.map(question => {
        if(question.labels && question.labels.length > 0){
          question.labels.map(label => {
            optionLabels.push(label);
          })
        }
      });
    });

    const optionList = Array.from(new Set(optionLabels));

    return optionList.map(option => {
      return {
        value: option,
        label: option,
        key: 'labelOptions',
        disabled: false,
      }
    })
  }

  const handleTabChange = (_: React.ChangeEvent<{}>, newIndex: number) => {
    setTabIndex(newIndex);
  };

  const handleSmallTabChange = (_: React.ChangeEvent<{}>, newIndex: number) => {
    setSmallTabIndex(newIndex);
  };

  const handleAddAnswer = () => {
    setFormQuestionState((prev) => {
      const updatedAnswers = [...(prev.answers || [])] as FbeQuestionAnswer[];

      updatedAnswers.push({answer:[{text:'',language:props.language}], action: '' as FbeQuestionAnswerAction, score:1});

      return { ...prev, answers: updatedAnswers };
    });
  }

  const handleAddTranslation = () => {
    setFormQuestionState((prev) => {
      const updatedVariableTranslation = [...(prev.variableTranslations || [])] as FbeVariableTranslations[];

      updatedVariableTranslation.push({key:'', type: FbeVariableType.STRING, value:''});

      return { ...prev, variableTranslations: updatedVariableTranslation };
    });
  };

  const handleRemoveAnswer = (externalIndex: number) => {

      const updatedQuestion = JSON.parse(JSON.stringify(formQuestionState)) as FbeQuestion;

      const updatedAnswers = updatedQuestion.answers || [] as FbeQuestionAnswer[];
      const updated = updatedAnswers.length > 1 ? updatedAnswers.splice(externalIndex, 1) : [];

      updatedQuestion.answers = updated;
      if(updatedQuestion.answers.length === 0){
        delete updatedQuestion.answers;
      }

      setFormQuestionState(updatedQuestion);
      props.onUpdateQuestion(updatedQuestion);
  }

  const handleRemoveTranslationData = (externalIndex: number) => {
    const updatedQuestion = JSON.parse(JSON.stringify(formQuestionState)) as FbeQuestion;

    const updatedVariableTranslation = updatedQuestion.variableTranslations || [] as FbeVariableTranslations[];
    const updated = updatedVariableTranslation.length > 1 ? updatedVariableTranslation.splice(externalIndex, 1) : [];

    updatedQuestion.variableTranslations = updated;
    if(updatedQuestion.variableTranslations.length === 0){
      delete updatedQuestion.variableTranslations;
    }

    setFormQuestionState(updatedQuestion);
    props.onUpdateQuestion(updatedQuestion);
  }

  const handleAddRowPosition = (
  ) => {
    setFormQuestionState((prev: FbeQuestion) => {
      let updatedQuestion: FbeQuestion = { ...prev };

      const rowPositionNames = [...(prev.rowPositionNames || [])] as FbeRowPositionName[];
      rowPositionNames.push({
          position: rowPositionNames.length + 1,
          name: [{language:props.language, text:''}]
        });
      updatedQuestion = {
          ...prev,
          rowPositionNames
      }
      props.onUpdateQuestion(updatedQuestion);
      return updatedQuestion;
    });
  }

  const handleAddDependency = (
      external: boolean,
    ) => {
      setFormQuestionState((prev: FbeQuestion) => {
        let updatedQuestion: FbeQuestion = { ...prev };

        if(external){
          const externalDependency = [...(prev.externalDependency || [])] as FbeDependency[];
          externalDependency.push({
            logicalOperator: LogicalOperator.AND,
            variable: '',
            operator: DependencyOperator.EQUALS
          });
          updatedQuestion = {
            ...prev,
            externalDependency
          }
          props.onUpdateQuestion(updatedQuestion);
        } else if(!external){
          const internalDependency = [...(prev.internalDependency || [])] as FbeDependency[];
          internalDependency.push({
            logicalOperator: LogicalOperator.AND,
            variable: '',
            operator: DependencyOperator.EQUALS
          });
          updatedQuestion = {
            ...prev,
            internalDependency
          }
          props.onUpdateQuestion(updatedQuestion);
        }
        return updatedQuestion;
      });
    }

    const handleRemoveRowPosition = (
      externalIndex: number
    ) => {
      const updatedQuestion: FbeQuestion = formQuestionState;
      if((updatedQuestion.rowPositionNames || []).length > externalIndex){
        updatedQuestion.rowPositionNames?.splice(externalIndex,1);
      }

      if(updatedQuestion.rowPositionNames?.length === 0){
        delete updatedQuestion.rowPositionNames;
      }

      props.onUpdateQuestion(updatedQuestion);
      return updatedQuestion;
    }

    const handleRemoveDependency = (
      external: boolean,
      externalIndex: number
    ) => {
      const updatedQuestion: FbeQuestion = formQuestionState;

      if(external && (updatedQuestion.externalDependency || []).length > externalIndex){
        updatedQuestion.externalDependency?.splice(externalIndex,1);
      } else if(!external && (updatedQuestion.internalDependency || []).length > externalIndex){
        updatedQuestion.internalDependency?.splice(externalIndex,1);
      }

      if(updatedQuestion.externalDependency?.length === 0){
        delete updatedQuestion.externalDependency;
      }

      if(updatedQuestion.internalDependency?.length === 0){
        delete updatedQuestion.internalDependency;
      }

      props.onUpdateQuestion(updatedQuestion);
      return updatedQuestion;
    }

  const removeElement = (question: FbeQuestion, field: string): FbeQuestion => {
    const updatedQuestion =
      Object.fromEntries(
        Object.entries(question).filter(([key]) => key !== field)
      ) as FbeQuestion;
     return updatedQuestion;
  }

  const handleLocalisedUpdate = (
    question: FbeQuestion,
    field: keyof FbeQuestion,
    language = props.language,
    value: string,
  ) : FbeQuestion => {
    const currentField = (question[field] || [] ) as LocalisedText[];

    // update field
    const updatedField = !currentField ? [] : currentField.map( element => {
      if(element.language === language){
        return {...element, text: value}
      } else {
        return element;
      }
    })

    // insert first time
    if(!updatedField.find(element => element.language === language)){
      updatedField.push({ language, text: value });
    }

    // remove empty values
    const filteredUpdatedField = updatedField.filter(info => info.text !== '')

    if(filteredUpdatedField.length > 0){
      return {
        ...question,
        [field]: filteredUpdatedField
      }
  } else{
    return {
      ...removeElement(question, field)
    }
  }

  }


  const isNumberField = (field: string): boolean => {
    return ['minAllowedValue','maxAllowedValue',
        'toleranceMinValue','tolerancePlusValue'
        ].includes(field);
  }

  const handleQuestionChange = (
    field: keyof FbeQuestion,
    value: unknown,
    language = props.language,
    index?: number,
    dependencyField?: string,
  ) => {
    setFormQuestionState((prev: FbeQuestion) => {
      let updatedQuestion: FbeQuestion = { ...prev };

      if( ['question', 'questionShort', 'additionalInfo', 'rowShort', 'columnShort'].includes(field)){
        updatedQuestion = handleLocalisedUpdate(prev, field as keyof FbeQuestion, language, value as string);
      } else if( ['actualValue','minAllowedValue','maxAllowedValue','minAllowedExternalVariable',
        'maxAllowedExternalVariable','minAllowedInternalVariable','maxAllowedInternalVariable',
        'toleranceMinValue','tolerancePlusValue','toleranceMinExternalVariable','tolerancePlusExternalVariable',
        'toleranceMinInternalVariable','tolerancePlusInternalVariable','multiAdjustmentDirection',
        'multiAdjustmentImpact','actualValueInternalVariable','pattern',
        'rowShort','columnShort','tableType','ascendingOrientation','ascendingOrientationInternalVariable',
        'ascendingOrientationInternalValue','externalVariable','internalVariable'
      ].includes(field)){
        updatedQuestion = prev;
        const key = field as keyof FbeQuestion;

        if (`${value}`.length === 0) {
          delete updatedQuestion[key];
        } else if (value !== undefined) {
          if(isNumberField(field)){
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            updatedQuestion[key] = Number(value) as number;
          } else{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            updatedQuestion[key] = value as string;
          }
        }
      } else if (field === 'labels') {
        updatedQuestion.labels = value as string[];
        if( updatedQuestion.labels.length === 0){
          delete updatedQuestion.labels
        }
      } else if (field === 'rowPositionNames') {
        const positionIndex = index || 0;
        if(positionIndex < (updatedQuestion.rowPositionNames || []).length){
          const rowPositionNames = prev.rowPositionNames || [];

          if(dependencyField === 'position'){
            rowPositionNames[positionIndex].position = Number(value);
          }
          else if(dependencyField === 'name'){
            rowPositionNames[positionIndex].name[0].text = value as string;
          }
          updatedQuestion = { ...prev, rowPositionNames };
        }
      } else if (field === 'answers') {
        const newValue = value as string;
        const updatedAnswers = prev.answers || [];

        const answerIndex = index || 0;

        if(answerIndex >= updatedAnswers.length){
          updatedAnswers.push({answer: [{language:props.language, text:''}], score: 1})
        }

        if(newValue.startsWith('V')){
          updatedAnswers[answerIndex].answer = [{language:props.language, text:newValue.substring(1)}];
        } else if(newValue.startsWith('A')){
          updatedAnswers[answerIndex].action = newValue.substring(1) as FbeQuestionAnswerAction;
        } else if(newValue.startsWith('S')){
          try{
            updatedAnswers[answerIndex].score = parseInt(newValue.substring(1));
          }catch(error){
            updatedAnswers[answerIndex].score = 1;
          }
        }

        updatedQuestion = { ...prev, answers: updatedAnswers };
      } else if (field === 'variableTranslations') {
          const updatedVariableTranslations = prev.variableTranslations || [];
          const newValue = `${value}`;
          if(newValue.startsWith('K')){
            updatedVariableTranslations[index || 0].key = newValue.substring(1);
          } else if(newValue.startsWith('T')){
            updatedVariableTranslations[index || 0].type = newValue.substring(1) as FbeVariableType;
          } else {
            switch(updatedVariableTranslations[index || 0].type || FbeVariableType.STRING){
              case FbeVariableType.BOOLEAN:
                updatedVariableTranslations[index || 0].value = `${newValue.substring(1)}` === 'true' ? true : false;
                break;
              case FbeVariableType.NUMBER:
                updatedVariableTranslations[index || 0].value = parseInt(`${newValue.substring(1)}`);
                break;
              default:
                updatedVariableTranslations[index || 0].value = `${newValue.substring(1)}`;
              }
          }
          updatedQuestion = { ...prev, variableTranslations: updatedVariableTranslations };
      } else if(field === 'internalDependency'){
      const internalDependency = updatedQuestion.internalDependency as FbeDependency[] || [{
        logicalOperator: LogicalOperator.AND,
        variable: '',
        operator: DependencyOperator.EQUALS,
        compareVariable: '',
        compareValue: ''
      }] as FbeDependency[];

      const dependencyIndex = index || 0;

      switch(dependencyField){
        case 'logicalOperator':
          internalDependency[dependencyIndex].logicalOperator = value as LogicalOperator;
          break
        case 'variable':
          internalDependency[dependencyIndex].variable = value as string;
          break
        case 'operator':
          internalDependency[dependencyIndex].operator = value as DependencyOperator;
          break
        case 'compareVariable':
          internalDependency[dependencyIndex].compareVariable = value as string;
          break
        case 'compareValue':
          internalDependency[dependencyIndex].compareValue = value as string;
          break
        default:
          break;
      }

      updatedQuestion[field] = internalDependency
    } else if(field === 'externalDependency'){
      const externalDependency = updatedQuestion.externalDependency as FbeDependency[] || [{
        logicalOperator: LogicalOperator.AND,
        variable: '',
        operator: DependencyOperator.EQUALS,
        compareVariable: '',
        compareValue: ''
      }] as FbeDependency[];

      const dependencyIndex = index || 0;

      switch(dependencyField){
        case 'logicalOperator':
          externalDependency[dependencyIndex].logicalOperator = value as LogicalOperator;
          break
        case 'variable':
          externalDependency[dependencyIndex].variable = value as string;
          break
        case 'operator':
          externalDependency[dependencyIndex].operator = value as DependencyOperator;
          break
        case 'compareVariable':
          externalDependency[dependencyIndex].compareVariable = value as string;
          break
        case 'compareValue':
          externalDependency[dependencyIndex].compareValue = value as string;
          break
        default:
          break;
      }

      updatedQuestion[field] = externalDependency
        } else if(field === 'maxAllowedValue'){
        if(`${value}` === ''){
          updatedQuestion = { ...prev}
          delete updatedQuestion.maxAllowedValue;
        } else {
          updatedQuestion = { ...prev, maxAllowedValue: value as number};
        }
      } else if(field === 'minAllowedValue'){
        if(`${value}` === ''){
          updatedQuestion = { ...prev}
          delete updatedQuestion.minAllowedValue;
        } else {
          updatedQuestion = { ...prev, minAllowedValue: value as number};
        }
      } else if(field === 'features'){
        const featureList = value as string[];
        updatedQuestion = { ...prev, features: featureList as FbeQuestionFeature[]}
        if(featureList.length === 0){
          delete updatedQuestion.features;
        }
      } else if(field === 'internalOutputVariable'){
        updatedQuestion = { ...prev, internalOutputVariable: value as string}
        if(`${value}`.length === 0){
          delete updatedQuestion.internalOutputVariable;
        }
      } else if(field === 'groupId'){
        updatedQuestion = { ...prev, groupId: value as string}
        if(`${value}`.length === 0){
          delete updatedQuestion.groupId;
        }
      } else if(field === 'mustScore'){
        updatedQuestion = { ...prev, mustScore: value as boolean}
        if(`${value as boolean}` === 'false'){
          delete updatedQuestion.mustScore;
          delete updatedQuestion.weight;
        }
      } else if(field === 'alternativeQuestionType'){
        updatedQuestion = { ...prev, alternativeQuestionType: value as FbeQuestionType}
        if(`${value}`.length === 0){
          delete updatedQuestion.alternativeQuestionType;
        }
      } else if(field === 'questionType'){
        updatedQuestion = { ...prev, questionType: value as FbeQuestionType}

        // Update advanced values
        if(updatedQuestion.questionType !== FbeQuestionType.NUMBER){
          delete updatedQuestion.actualValue;
          delete updatedQuestion.minAllowedValue;
          delete updatedQuestion.maxAllowedValue;
          delete updatedQuestion.minAllowedExternalVariable;
          delete updatedQuestion.maxAllowedExternalVariable;
          delete updatedQuestion.minAllowedInternalVariable;
          delete updatedQuestion.maxAllowedInternalVariable;
          delete updatedQuestion.toleranceMinValue;
          delete updatedQuestion.tolerancePlusValue;
          delete updatedQuestion.toleranceMinExternalVariable;
          delete updatedQuestion.tolerancePlusExternalVariable;
          delete updatedQuestion.toleranceMinInternalVariable;
          delete updatedQuestion.tolerancePlusInternalVariable;
          delete updatedQuestion.multiAdjustmentDirection;
          delete updatedQuestion.multiAdjustmentImpact;
          delete updatedQuestion.actualValueInternalVariable;
        }
        if(updatedQuestion.questionType !== FbeQuestionType.TEXT){
          delete updatedQuestion.pattern;
        }
        if(updatedQuestion.questionType !== FbeQuestionType.NUMBER_TABLE){
          delete updatedQuestion.rowShort;
          delete updatedQuestion.columnShort;
          delete updatedQuestion.ascendingOrientation;
          delete updatedQuestion.ascendingOrientationInternalVariable;
          delete updatedQuestion.ascendingOrientationInternalValue;
          delete updatedQuestion.rowPositionNames;
          delete updatedQuestion.tableType;
        }
      } else if(field === 'externalVariable'){
        updatedQuestion = { ...prev, externalVariable: (value as string)}
      } else {
        updatedQuestion = { ...prev, [field]: value };
      }

      // Call parent function to update `formSections`
      props.onUpdateQuestion(updatedQuestion);
      return updatedQuestion;
    });
  };
  useLayoutEffect(() => {
    const currentImage = props.question.references &&
    props.question.references[0] ?
    props.question.references[0].location : '';

    setSelected(currentImage);
    setQuestionImage(currentImage);
    setTimestamp('');
    setCurrentElement(props.question.questionId)
  }, []);
/*
  useEffect(() => {

    const currentQuestionImage = (props.question.references &&
    props.question.references[0]) ?
    props.question.references[0].location : '';

    if(currentQuestionImage !== questionImage && timestamp.length > 1 &&
      currentElement === props.question.questionId
    ){
      setSelected(questionImage);
      setQuestionImage(questionImage);

      handleQuestionImageChange('image', questionImage)
    }
  }, [timestamp]);
*/
  useEffect(() => {
    const invalidSections = props.formSections.filter(formSection => !isSectionValid(formSection,[]))
    const isValid = isQuestionValid(formQuestionState,[]) && invalidSections.length === 0

    setFormQuestionValid(isValid);
    // Check if any changes were made
    const isChanged =
      !deepEqual(formQuestionState,props.originalQuestion);
    setChangesMade(isChanged);
  }, [formQuestionState, props.originalQuestion]);

  const giFeatureTitle='Features (optional)';
  const giFeatureBody='Select features that apply to the question.<><>Create deviation to display a create deviation button.<><>Mandatory question (extend) to indicate when this form is extended the question must be there.<><>Manipulate in table to indicate this number question can be manipulated in table form.<><> Is maximum X tolerance indicates that the answer to this question contains the maximum X tolerance value in a table.<><> Is maximum Y tolerance indicates that the answer to this question contains the maximum Y tolerance value in a table.<><>Is minimum X tolerance indicates that the answer to this question contains the minimum X tolerance value in a table.<><>Is minimum Y tolerance indicates that the answer to this question contains the minimum Y tolerance value in a table.<><>Must answer indicates that this questions must be answered by user before ending the form.<><>Unique to indicate all answers needs to be unique (specially for loop questions).';

  const giQuestionTextHelpTitle='Question (mandatory)';
  const giQuestionTextHelpBody='Enter the question to be displayed to user here.';
  const giQuestionShortTextHelpTitle='Question short (optional)';
  const giQuestionShortTextHelpBody='When displaying a question in table format this short description is used as column name.';

  const giOrientationHelpTitle='Table orientation (optional)';
  const giOrientationHelpBody='Set the default orientation for the table.';
  const giOrientationInternalVariableHelpTitle='Table orientation internal variable (optional)';
  const giOrientationInternalVariableHelpBody='Select an internal variable to use to determine the orientation dynamically.';
  const giOrientationInternalValueHelpTitle='Table orientation internal value (optional)';
  const giOrientationInternalValueHelpBody='Select the internal variable value to use for an ascending orientation.';

  const giRowShortTextHelpTitle='Table row name (optional)';
  const giRowShortTextHelpBody='The row type in a table view, keep it short.';
  const giColumnShortTextHelpTitle='Table column name (optional)';
  const giColumnShortTextHelpBody='The column type in a table view, keep it short.';

  const giAdditionalHelpTitle='Additional info (optional)';
  const giAdditionalHelpBody='Enter additional info that can be displayed with the question.';
  const giOrderHelpTitle='Order (mandatory)';
  const giOrderHelpBody='This field displays the order of the question. <><>Drag the questions at the left menu bar to change the order.';

  const giInternalOutputVariableHelpTitle='Internal output variable (optional)';
  const giInternalOutputVariableHelpBody='The answer of a question can be used as input or dependency for another question. Define a unique name which the displaying application will use to store the value.';

  const dQuestionTypeHelpTitle='Question type (mandatory)';
  const dQuestionTypeHelpBody='Select the desired question type. <><>When a question is postfixed with (action) the displaying application is encouraged to perform an action, for example IES creates a deviation, when the answer is not positive. <><>When a question is postfixed with (score) then the question counts as a scoring question. <><>Use Info to display information (dummy question).<><>Number for measurements, it becomes a scoring question when minimum and maximum values are provided.<><>Picture to upload an attachment. <><>Select ... to display predefined choices.';
  const dAlternativeQuestionTypeHelpTitle='Alternative question type (optional)';
  const dAlternativeQuestionTypeHelpBody='When the dependency fails an alternative question type can be displayed instead of hiding the original question. <><> When a question is postfixed with (action) the displaying application is encouraged to perform an action, for example IES creates a deviation, when the answer is not positive. <><>When a question is postfixed with (score) then the question counts as a scoring question.<><>Use Info to display information (dummy question).<><>Number for measurements, it becomes a scoring question when minimum and maximum values are provided.<><>Picture to upload an attachment. <><>Select ... to display predefined choices.';

  const dGroupIdTextHelpTitle = 'Group ID (optional)';
  const dGroupIdTextHelpBody = 'Use a group ID on question level to relate questions to eachother. <><>A usecase for this if you have measurement questions which you would like to display in a table.';

  const dExternalVariableHelpTitle = 'Answer by external variable (optional)';
  const dExternalVariableHelpBody = 'Select an external variable from the mapping table from which the value is used as a default answer for the question.';
  const dInternalVariableHelpTitle = 'Answer by internal variable (optional)';
  const dInternalVariableHelpBody = 'Select an internal variable from other questions from which the value is used as a default answer for the question.';

  const dAddAnswerTitle = 'New answer (optional)';
  const dAddAnswerBody = 'Add multiple answers for a Select question.';

  const dAddTitle = 'New translation (optional)';
  const dAddBody = 'When selecting a variable as a default answer value for a question, the actual value might not be suitable for display. In such a case a list of translations can provided to translate the raw data into user friendly data.';
  const dKeyTitle = 'Key (mandatory)';
  const dKeyBody = 'Provide an expected (raw) value that needs a translation.';
  const dValueTitle = 'Value (mandatory)';
  const dValueBody = 'Provide the new value to be translated to.';
  const dTypeTitle = 'Type (mandatory)';
  const dTypeBody = 'Provide the type of the value. Incorrect type might influence the usage of the variable.';

  const dRemoveTranslationTitle = 'Remove translation (optional)';
  const dRemoveTranslationBody = 'Remove above translation.';

  const dLabelsHelpTitle = 'Labels (optional)';
  const dLabelsHelpBody = 'Add one or more labels to a question.<><>Labels enable the viewer to perform label specific actions.';

  const dLoopRepeatHelpTitle = 'Loop repeat (mandatory)';
  const dLoopRepeatHelpBody = 'Sets the loop behavior on question level in a loop section.<><>After is displayed after the loop.<><>Before is displayed before the loop.<><>Repeat questions are displayed in the loop.';

  const dAnswerValueHelpTitle = 'Answer value (optional)';
  const dAnswerValueHelpBody = 'Define an answer option.';
  const dAnswerActionHelpTitle = 'Answer action (optional)';
  const dAnswerActionHelpBody = 'Select an optional action when selecting this answer.<><>Select create deviation to create a new deviation.';
  const dAnswerScoreHelpTitle = 'Answer score (optional)';
  const dAnswerScoreHelpBody = 'Add a score to this answer.';

  const dRemoveAnswerTitle = 'Remove answer (optional)';
  const dRemoveAnswerBody = 'Click the text to remove the above answer.';

  const sWeightTitle = 'Weight (optional)';
  const sWeightBody = 'Default weight for a scoring question is 1. <><>Enter a higher number to increase the weight for this question.';
  const sMustPassTitle = 'Must pass (optional)';
  const sMustPassBody = 'Enabling indicates that this is an essential question. In case of a negative answer the form will fail.';

  const sdLogicalOperatorTitle = 'Logical operator (mandatory)';
  const sdLogicalOperatorBody = 'Select the desired operator.<><>AND if all dependecies must apply.<><>OR if one needs to apply.';
  const sdVariableTitle = 'Variable (mandatory)';
  const sdVariableBody = 'Select the (external/ internal) variable for the dependency.';
  const sdOperatorTitle = 'Operator (mandatory';
  const sdOperatorBody = 'Select the desired operator.<><>Equals, Not equals, Includes, Not includes, Greater or Smaller.';
  const sdCompareValueTitle = 'Compare value (optional)';
  const sdCompareValueBody = 'Compare the selected variable against this value';
  const sdCompareVariableTitle = 'Compare variable (optional)';
  const sdCompareVariableBody = 'Compare the selected variable against this variable.';
  const sdRemoveDependencyTitle = 'Remove dependency';
  const sdRemoveDependencyBody = 'Deletes above dependency.';
  const sdAddDependencyTitle = 'Add dependency';
  const sdAddDependencyBody = 'Add a default dependency. Then update the fields.';
  const nlMinValueTitle = 'Minimum value (optional)';
  const nlMinValueBody = 'To enable display application validation and make a number question scoring provide minimum (and maximum) value.';
  const nlMinExternalVariableTitle = 'Minimum external variable (optional)';
  const nlMinExternalVariableBody = 'Select an external variable as a minimum value. It is good practice also to provide the minimum value as a default in case the variable does not exist.';
  const nlMinInternalVariableTitle = 'Minimum internal variable (optional)';
  const nlMinInternalVariableBody = 'Select an internal variable as a minimum value. It is good practice also to provide the minimum value as a default in case the variable does not exist.';

  const nlMaxValueTitle = 'Maximum value (optional)';
  const nlMaxValueBody = 'To enable display application validation and make a number question scoring provide maximum (and minimum) value.';
  const nlMaxExternalVariableTitle = 'Maximum external variable (optional)';
  const nlMaxExternalVariableBody = 'Select an external variable as a maximum value. It is good practice also to provide the maximum value as a default in case the variable does not exist.';
  const nlMaxInternalVariableTitle = 'Maximum internal variable (optional)';
  const nlMaxInternalVariableBody = 'Select an internal variable as a maximum value. It is good practice also to provide the maximum value as a default in case the variable does not exist.';

  const nlMinTolValueTitle = 'Negative tolerance value (optional)';
  const nlMinTolValueBody = 'The negative tolerance value contains the maximum amount that can be subtracted from the (expected) mean value.';
  const nlMinTolExternalVariableTitle = 'Negative tolerance external variable (optional)';
  const nlMinTolExternalVariableBody = 'The negative tolerance external variable contains the external variable that contains the maximum amount that can be subtracted from the (expected) mean value.';
  const nlMinTolInternalVariableTitle = 'Negative tolerance internal variable (optional)';
  const nlMinTolInternalVariableBody = 'The negative tolerance internal variable contains the internal variable that contains the maximum amount that can be subtracted from the (expected) mean value.';

  const nlMaxTolValueTitle = 'Positive tolerance value (optional)';
  const nlMaxTolValueBody = 'The positive tolerance value contains the maximum amount that can be added to the (expected) mean value.';
  const nlMaxTolExternalVariableTitle = 'Positive tolerance external variable (optional)';
  const nlMaxTolExternalVariableBody = 'The positive tolerance external variable contains the external variable that contains the maximum amount that can be added to the (expected) mean value.';
  const nlMaxTolInternalVariableTitle = 'Positive tolerance internal variable (optional)';
  const nlMaxTolInternalVariableBody = 'The positive tolerance internal variable contains the internal variable that contains the maximum amount that can be added to the (expected) mean value.';

  const nlActualValueTitle = 'Mean value (optional)';
  const nlActualValueBody = 'Provide the mean value, which works together with the tolerances in the displaying application.';
  const nlMultiAdjustmentDirectionTitle = 'Adjust direction (optional)';
  const nlMultiAdjustmentDirectionBody = 'Specifies in which direction the value can be adjusted.<><>Select Horizontal or Vertical directon in a two axis plane.';
  const nlMultiAdjustmentImpactTitle = 'Adjust impact (optional)';
  const nlMultiAdjustmentImpactBody = 'Specifies how the adjustment impacts the value.<><>Opposite means the sign is reversed, +1 becomes -1 and vice versa. Follow direction follows the sign.';
  const nlActualValueInternalVariableTitle = 'Mean internal variablet (optional)';
  const nlActualValueInternalVariableBody = 'Use an internal variable as mean, together with tolerances to get a dynamic min and max number value.';

  const nlPatternTitle = 'Pattern(optional)';
  const nlPatternBody = 'Provide a regular expression to which a string answer must match.';

  const aTableTypeHelpTitle = 'Table type (optional)';
  const aTableTypeHelpBody = 'Select Plumbing for a 2D table with adjustments.<><>Select Regular to manipulate numbers in a table fashion.';

  const aAddRowPositionTitle = 'Add row position and name';
  const aAddRowPositionBody = 'Add a position and name for a number table row.<><>Total should be matching the loop total.';

  const aRemoveRowPositionNameTitle = 'Remove row position and name';
  const aRemoveRowPositionNameBody = 'Deletes above row position and name.';

  const aRowPositionTitle = 'Row position (mandatory)';
  const aRowPositionBody = 'Provide the row number. Start at 1.';

  const aRowPositionNameTitle = 'Row name (mandatory)';
  const aRowPositionNameBody = 'Provide the row name.';


  const scoringQuestions = [FbeQuestionType.MULTI_SELECT,FbeQuestionType.SELECT,FbeQuestionType.NUMBER,FbeQuestionType.SELECT_ONO,FbeQuestionType.SELECT_ONONA,FbeQuestionType.SELECT_WARNING_ONONA, FbeQuestionType.SELECT_YN];
  const dynamicAnswerQuestions = [FbeQuestionType.MULTI_SELECT,FbeQuestionType.SELECT,FbeQuestionType.CHOICE];

  const isLoop = (props.sectionIndex < props.formSections.length) && props.formSections[props.sectionIndex].loop;

  const questionSupportOwnAnswers = dynamicAnswerQuestions.includes(formQuestionState.questionType);

  const validGeneralInfo = isQuestionValid(formQuestionState,['question']);
  const validDetail = isQuestionValid(formQuestionState,['questionType','loopRepeat']);
  const validScoring = true;
  const validDependency = isQuestionValid(formQuestionState,['externalDependency','internalDependency','variableTranslations']);
  const validReferences = isQuestionValid(formQuestionState,['references']);

  let validTemp = true;
  if(formQuestionState && formQuestionState.questionType === FbeQuestionType.NUMBER_TABLE){
    validTemp = isQuestionValid(formQuestionState,['tableType','rowPositionNames']);
  }
  const validAdvanced = validTemp;

  const changedGeneralInfo = hasQuestionChanges(props.originalQuestion,formQuestionState,['question','features','additionalInfo','order','internalOutputVariable']);
  const changedDetail = hasQuestionChanges(props.originalQuestion,formQuestionState,['questionType','loopRepeat','labels','externalVariable','internalVariable','questionShort','groupId','answers']);
  const changedScoring = hasQuestionChanges(props.originalQuestion,formQuestionState,['weight','mustScore']);
  const changedDependency = hasQuestionChanges(props.originalQuestion,formQuestionState,['externalDependency','internalDependency','alternativeQuestionType','variableTranslations']);
  const changedReferences = hasQuestionChanges(props.originalQuestion,formQuestionState,['references']);
  const changedAdvanced = hasQuestionChanges(props.originalQuestion,formQuestionState,['tableType','rowPositionNames','rowShort','columnShort','ascendingOrientation','ascendingOrientationInternalVariable','ascendingOrientationInternalValue','minAllowedValue','maxAllowedValue','minAllowedExternalVariable','maxAllowedExternalVariable','minAllowedInternalVariable','maxAllowedInternalVariable','toleranceMinValue','tolerancePlusValue','toleranceMinExternalVariable','tolerancePlusExternalVariable','toleranceMinInternalVariable','tolerancePlusInternalVariable','multiAdjustmentDirection','multiAdjustmentImpact','actualValue','actualValueInternalVariable','pattern']);

  const currentLanguages: string[] = [];
  if(props.form && props.form.name && props.form.name.length > 0){
    props.form.name.map(element => {currentLanguages.push(element.language)})
  }

  const baseLanguage = currentLanguages.length > 0 ? currentLanguages[0] : 'invalid';

  const readOnlyQuestion = props.readOnly || (
    (formQuestionState.features ? formQuestionState.features.includes(FbeQuestionFeature.EXTEND_STATIC_QUESTION) : false)
    && props.form?.pk !== 'form_global'
  );

  const referenceImageList: string[] = []

  formQuestionState.references?.map(reference => {
    if(reference.location &&
      (reference.type === FbeReferenceType.IMAGE || reference.type === FbeReferenceType.TIP_IMAGE)
    ){
      referenceImageList.push(reference.location)
    }
  });

  const handleOnAddImage = (image: string): void => {
    const updatedQuestion = JSON.parse(JSON.stringify(formQuestionState)) as FbeQuestion;
    if(!updatedQuestion.references){
      updatedQuestion.references = [];
    }
    updatedQuestion.references.push({
      type: FbeReferenceType.IMAGE,
      location: image
    });

    setFormQuestionState(updatedQuestion);
    props.onUpdateQuestion(updatedQuestion);
  }

  const handleOnRemoveImage = (image: string): void => {
    const updatedQuestion = JSON.parse(JSON.stringify(formQuestionState)) as FbeQuestion;
    if(!updatedQuestion.references){
      updatedQuestion.references = [];
    }
    const updatedReferences = updatedQuestion.references.filter( reference =>
      reference.location !== image
    );
    updatedQuestion.references = updatedReferences;

    if(updatedQuestion.references.length === 0){
      delete updatedQuestion.references;
    }

    setFormQuestionState(updatedQuestion);
    props.onUpdateQuestion(updatedQuestion);
  }

  const handleAllLockedFields = (
    locked: boolean
  ) => {
    const { lockedFields, ...prev} = formQuestionState;
    let updatedLockedFields = lockedFields || [];
    if(locked){
      updatedLockedFields = JSON.parse(JSON.stringify(fbeQuestionFields)) as string[];
    } else {
      updatedLockedFields = [];
    }

    if(updatedLockedFields.length > 0){
      setFormQuestionState({...prev, lockedFields: updatedLockedFields});
    } else {
      setFormQuestionState(prev);
    }
  }

  const handleLockedFields = (
    field: string,
    locked: boolean
  ) => {
    const { lockedFields, ...prev} = formQuestionState;
    let updatedLockedFields = lockedFields || [];
    if(locked){
      updatedLockedFields.push(field);
    } else {
      updatedLockedFields = updatedLockedFields.filter(updatedLockedField => updatedLockedField !== field);
    }

    updatedLockedFields = Array.from(new Set(updatedLockedFields));

    const updatedQuestion = updatedLockedFields.length > 0 ? {...prev, lockedFields: updatedLockedFields.sort()} : prev;
    setFormQuestionState(updatedQuestion);
    props.onUpdateQuestion(updatedQuestion);

  }

  const locksDisabled =  !formQuestionState || (
      props.form?.pk !== 'form_global' && props.form?.type === FbeType.FULL_MERGE);

  const locksDisplayed = formQuestionState && (
      props.form?.pk === 'form_global' || props.form?.type === FbeType.FULL_MERGE);

      return (
    <>
        <Grid2 size={{ xs: 12, sm: 8, md: 9 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ backgroundColor: '#f5f5f5', padding: '8px' }}
          >
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label="form tabs"
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EditOutlined sx={{ marginRight: '8px' }} />
                    Edit
                  </Box>
                }
              />
            </Tabs>

            <Box
              display="flex"
              alignItems="center"
              data-testid="language-select"
            >
              <Typography variant="body2" sx={{ marginRight: '8px' }}>
                Localization:
              </Typography>
              <Select
                value={props.language}
                sx={{ minWidth: 120 }}
                disabled={true}
              >
              {
              props.supportedLanguages.length > 0 ? props.supportedLanguages.map(languageOption => (
                  <MenuItem value={languageOption} selected={props.language === languageOption} data-testid={`form-language-${languageOption}`}>
                    {getLanguageDisplayName(languageOption, props.frontline?.settings.languageDisplayNames || {})} { currentLanguages.includes(languageOption) ? (baseLanguage === languageOption ? '(*)' : '(T)') : ''}
                  </MenuItem>
              )) : (<></>)}
              </Select>            </Box>
          </Box>
          <Box mt={3} sx={{ paddingLeft: '24px', paddingRight: '24px'}}>
            {tabIndex === 0 && (
              <>
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">
                      <FieldLock
                        onChange={function (locked: boolean): void {
                            handleAllLockedFields(locked);
                        } }
                        labelId={'all'}
                        disabled={locksDisabled}
                        displayed={locksDisplayed}
                        size={24}
                        value={(formQuestionState.lockedFields || []).length === fbeQuestionFields.length}/>
                        Edit Question</Typography>
                  </Box>
                  <Box mt={2}>
                  {/* <InputFieldResize
                    labelText={'Question'}
                    labelId={'question'}
                    mandatory={true}
                    helpTextTitle={giQuestionTextHelpTitle}
                    helpTextBody={giQuestionTextHelpBody}
                    onChange={(value) =>
                      handleQuestionChange('question', value)
                    }
                    onBlur={() =>
                      setTouchedFields((prev) => ({
                        ...prev,
                        question: true,
                      }))
                    }
                    fullWidth={true}
                    value={formQuestionState.question?.[0]?.text || ''}
                    error={
                      touchedFields.question &&
                      !formQuestionState.question[0].text
                    }
                    helperText={
                      touchedFields.question &&
                      !formQuestionState.question[0].text
                        ? 'Question is required'
                        : ''
                    }
                    disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('question') )}
                    onLock={handleLockedFields}
                    disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                    isLocked={(formQuestionState.lockedFields || []).includes('question')}
                  /> */}
                  <RichTextField
                    labelText={'Question'}
                    labelId={'question'}
                    mandatory={true}
                    maxWords={100}
                    helpTextTitle={giQuestionTextHelpTitle}
                    helpTextBody={giQuestionTextHelpBody}
                    onChange={(value) =>
                      handleQuestionChange('question', value)
                    }
                    onBlur={() =>
                      setTouchedFields((prev) => ({
                        ...prev,
                        question: true,
                      }))
                    }
                    fullWidth={true}
                    value={formQuestionState.question?.[0]?.text || ''}
                    error={
                      touchedFields.question &&
                      !formQuestionState.question[0].text
                    }
                    helperText={
                      touchedFields.question &&
                      !formQuestionState.question[0].text
                        ? 'Question is required'
                        : ''
                    }
                    disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('question') )}
                    onLock={handleLockedFields}
                    disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                    locked={(formQuestionState.lockedFields || []).includes('question')}
                  />
                  {/* Add spacing below the question field */}
                  <Box sx={{ mb: 3 }} />
                  </Box>
                    <SmallTabBoxStyleStyled>
                    <Tabs
                      value={smallTabIndex}
                      onChange={handleSmallTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ flexGrow: 1, minWidth: 0 }}
                    >
                      <Tab
                        label="General Info"
                        sx={{
                          ...( changedGeneralInfo ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validGeneralInfo ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto',
                          width: 'auto',
                          flex: 1,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      />
                      <SmallTabStyleStyled
                        label="Details"
                        sx={{
                          ...( changedDetail ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validDetail ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                      <SmallTabStyleStyled
                        label="Scoring"
                        sx={{
                          ...( changedScoring ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validScoring ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                      <SmallTabStyleStyled
                        label="Dependency"
                        sx={{
                          ...( changedDependency ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validDependency ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                      <SmallTabStyleStyled
                        label="References"
                        sx={{
                          ...( changedReferences ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validReferences ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                      <SmallTabStyleStyled
                        label="Advanced"
                        sx={{
                          ...( changedAdvanced ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validAdvanced ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                    </Tabs>
                    </SmallTabBoxStyleStyled>

                  <EditContainer>
                  {smallTabIndex === 0 && (
                    <Box>
                    {/* General info */}

                    {/* Features */}
                    <InputRowContainer>
                      <MultiSelect
                        labelText={'Feature(s)'}
                        labelId={'features'}
                        mandatory={false}
                        fullWidth={true}
                        helpTextTitle={giFeatureTitle}
                        helpTextBody={giFeatureBody}
                        onChange={(value) => {
                          handleQuestionChange('features', value);
                          setTouchedFields((prev) => ({
                            ...prev,
                            features: true,
                          })); // Mark as touched
                        }}
                        options={questionFeatureOptions}
                        selectedValues={questionFeatureOptions.filter(option => `${formQuestionState.features}`.includes(option.value))}
                        disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('features') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('features')}
                      />
                    </InputRowContainer>

                    {/* Additional info */}
                    <InputRowContainer>
                    {/* <InputField
                      labelText={'Additional info'}
                      labelId={'additionalInfo'}
                      mandatory={false}
                      helpTextTitle={giAdditionalHelpTitle}
                      helpTextBody={giAdditionalHelpBody}
                      value={
                        formQuestionState.additionalInfo?.[0]?.text || ''
                      }
                      onChange={(value) =>
                        handleQuestionChange(
                          'additionalInfo',
                          value
                        )
                      }
                      onBlur={() =>
                        setTouchedFields((prev) => ({
                          ...prev,
                          additionalInfo: true,
                        }))
                      }
                      fullWidth={true}
                      rows={4}
                      error={false}
                      helperText={''}
                      disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('additionalInfo') )}
                      onLock={handleLockedFields}
                      disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                      isLocked={(formQuestionState.lockedFields || []).includes('additionalInfo')}
                    /> */}
                    <RichTextField
                      labelText={'Additional info'}
                      labelId={'additionalInfo'}
                      mandatory={false}
                      helpTextTitle={giAdditionalHelpTitle}
                      helpTextBody={giAdditionalHelpBody}
                      value={formQuestionState.additionalInfo?.[0]?.text || ''}
                      onChange={(value) =>
                        handleQuestionChange('additionalInfo', value)
                      }
                      onBlur={() =>
                        setTouchedFields((prev) => ({
                          ...prev,
                          additionalInfo: true,
                        }))
                      }
                      fullWidth={true}
                      error={false}
                      helperText={''}
                      disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('additionalInfo') )}
                      onLock={handleLockedFields}
                      disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                      locked={(formQuestionState.lockedFields || []).includes('additionalInfo')}
                    />
                    </InputRowContainer>
                    <InputRowContainer>
                      <InputField
                        labelText={'Order'}
                        labelId={'order'}
                        mandatory={true}
                        helpTextTitle={giOrderHelpTitle}
                        helpTextBody={giOrderHelpBody}
                        value={formQuestionState.order}
                        onChange={(ivalue) => {
                          const value = ivalue.trim(); // Remove whitespace
                          const numericValue =
                            value === '' ? null : Number(value); // Convert to number or null

                          if (
                            numericValue === null ||
                            (!isNaN(numericValue) && numericValue >= 0)
                          ) {
                            handleQuestionChange(
                              'order',
                              numericValue as number
                            ); // Ensure it's always a number
                          }
                        }}
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            order: true,
                          }))
                        }
                        fullWidth={false}
                        error={
                          touchedFields.order &&
                          (formQuestionState.order === null ||
                            isNaN(Number(formQuestionState.order)))
                        }
                        helperText={
                          touchedFields.order &&
                          (formQuestionState.order === null ||
                            isNaN(Number(formQuestionState.order)))
                            ? 'Order is required and must be a valid number'
                            : ''
                        }
                        type="number"
                        disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('order') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('order')}
                      />
                      <InputField
                        labelText={'Internal output variable'}
                        labelId={'internalOutputVariable'}
                        mandatory={false}
                        helpTextTitle={giInternalOutputVariableHelpTitle}
                        helpTextBody={giInternalOutputVariableHelpBody}
                        value={formQuestionState.internalOutputVariable || ''}
                        onChange={(value) =>
                          handleQuestionChange('internalOutputVariable', value)
                        }
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            internalOutputVariable: true,
                          }))
                        }
                        fullWidth={true}
                        error={false}
                        helperText={''}
                        disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('internalOutputVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('internalOutputVariable')}
                      />
                    </InputRowContainer>
                    </Box>
                  )}
                  { smallTabIndex === 1 && (
                  <Box>
                    {/* Details */}
                    <InputRowContainer>
                      <SingleSelect
                        labelText={'Question type'}
                        labelId={'questionType'}
                        mandatory={true}
                        fullWidth={true}
                        helpTextTitle={dQuestionTypeHelpTitle}
                        helpTextBody={dQuestionTypeHelpBody}
                        onChange={(value) =>
                          handleQuestionChange('questionType', value)
                        }
                        options={questionTypeOptions}
                        selectedValues={questionTypeOptions.filter(option => formQuestionState.questionType === (option.value))}
                        disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('questionType') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('questionType')}
                      />
                      <MultiSelectFree
                        labelText={'Labels'}
                        labelId={'labels'}
                        mandatory={false}
                        fullWidth={true}
                        helpTextTitle={dLabelsHelpTitle}
                        helpTextBody={dLabelsHelpBody}
                        onChange={(value) =>
                          handleQuestionChange('labels', value)
                        }
                        options={generateLabelOptions(formQuestionState.labels || [])}
                        selectedValues={generateLabelOptions(formQuestionState.labels || []).filter(option => `${formQuestionState.labels}`.includes(option.value))}
                        disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('labels') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('labels')}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                      <SingleSelect
                        labelText={'External variable'}
                        labelId={'externalVariable'}
                        mandatory={false}
                        fullWidth={true}
                        helpTextTitle={dExternalVariableHelpTitle}
                        helpTextBody={dExternalVariableHelpBody}
                        onChange={(value) =>
                          handleQuestionChange('externalVariable', value)
                        }
                        options={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable')}
                        selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable').filter(option => formQuestionState.externalVariable === option.value)}
                        disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('externalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('externalVariable')}
                      />
                      <SingleSelect
                        labelText={'Internal variable'}
                        labelId={'internalVariable'}
                        mandatory={false}
                        fullWidth={true}
                        helpTextTitle={dInternalVariableHelpTitle}
                        helpTextBody={dInternalVariableHelpBody}
                        onChange={(value) =>
                          handleQuestionChange('internalVariable', value)
                        }
                        options={generatInternalVariableToOptions(props.formSections || [], 'internalVariable')}
                        selectedValues={generatInternalVariableToOptions(props.formSections || [], 'internalVariable').filter(option => formQuestionState.internalVariable === option.value)}
                        disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('internalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('internalVariable')}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                    <InputField
                      labelText={'Question short'}
                      labelId={'questionShort'}
                      mandatory={false}
                      helpTextTitle={giQuestionShortTextHelpTitle}
                      helpTextBody={giQuestionShortTextHelpBody}
                      onChange={(value) =>
                        handleQuestionChange('questionShort', value)
                      }
                      onBlur={() =>
                        setTouchedFields((prev) => ({
                          ...prev,
                          questionShort: true,
                        }))
                      }
                      fullWidth={true}
                      value={formQuestionState.questionShort?.[0]?.text || ''}
                      error={false}
                      helperText={''}
                      disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('questionShort') )}
                      onLock={handleLockedFields}
                      disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                      isLocked={(formQuestionState.lockedFields || []).includes('questionShort')}
                    />
                    <InputField
                        labelText={'Group ID'}
                        labelId={'groupId'}
                        mandatory={false}
                        helpTextTitle={dGroupIdTextHelpTitle}
                        helpTextBody={dGroupIdTextHelpBody}
                        value={formQuestionState.groupId || ''}
                        onChange={(value) =>
                          handleQuestionChange('groupId', value)
                        }
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            groupId: true,
                          }))
                        }
                        fullWidth={true}
                        error={false}
                        helperText={''}
                        disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('groupId') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('groupId')}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                    <SingleSelect
                        labelText={'Loop repeat'}
                        labelId={'loopRepeat'}
                        mandatory={true}
                        fullWidth={true}
                        helpTextTitle={dLoopRepeatHelpTitle}
                        helpTextBody={dLoopRepeatHelpBody}
                        onChange={(value) =>
                          handleQuestionChange('loopRepeat', value)
                        }
                        options={questionLoopRepeatOptions}
                        selectedValues={questionLoopRepeatOptions.filter(option => (formQuestionState.loopRepeat || 'repeat') === (option.value))}
                        disabled={!isLoop || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('loopRepeat') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('loopRepeat')}
                      />
                    </InputRowContainer>
                    { (formQuestionState.answers || []).length > 0 &&
                    <>
                      <p>Answers</p>
                      <LineSeparator/>
                      </>
                    }

                    { (formQuestionState.answers || []).map(
                      (data, answerIndex) => (
                      <>
                        <InputRowContainer>
                          <InputField
                            labelText={'Answer'}
                            labelId={`${answerIndex}answer}`}
                            mandatory={true}
                            helpTextTitle={dAnswerValueHelpTitle}
                            helpTextBody={dAnswerValueHelpBody}
                            value={data.answer[0].text || ''}
                            onChange={(value) =>
                              handleQuestionChange('answers', `V${value}`, props.language, answerIndex)
                            }
                            onBlur={() =>
                              setTouchedFields((prev) => ({
                                ...prev,
                                answers: true,
                              }))
                            }
                            fullWidth={false}
                            error={false}
                            helperText={''}
                            disabled={!dynamicAnswerQuestions.includes(formQuestionState.questionType) || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('answers') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                            isLocked={(formQuestionState.lockedFields || []).includes('answers')}
                          />
                          <SingleSelect
                            labelText={'Action'}
                            labelId={`${answerIndex}action}`}
                            mandatory={false}
                            fullWidth={false}
                            helpTextTitle={dAnswerActionHelpTitle}
                            helpTextBody={dAnswerActionHelpBody}
                            onChange={(value) =>
                              handleQuestionChange('answers', `A${value}`, props.language, answerIndex)
                            }
                            options={answerActionOptions}
                            selectedValues={answerActionOptions.filter(option => (data.action || '') === option.value)}
                            disabled={!dynamicAnswerQuestions.includes(formQuestionState.questionType) || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('answers') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                            isLocked={(formQuestionState.lockedFields || []).includes('answers')}
                          />
                          <InputField
                            labelText={'Score'}
                            labelId={`${answerIndex}score}`}
                            mandatory={false}
                            helpTextTitle={dAnswerScoreHelpTitle}
                            helpTextBody={dAnswerScoreHelpBody}
                            value={data.score || 1}
                            onChange={(value) =>
                              handleQuestionChange('answers', `S${value}`, props.language, answerIndex)
                            }
                            onBlur={() =>
                              setTouchedFields((prev) => ({
                                ...prev,
                                answers: true,
                              }))
                            }
                            fullWidth={false}
                            error={false}
                            helperText={''}
                            disabled={!dynamicAnswerQuestions.includes(formQuestionState.questionType) || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('answers') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                            isLocked={(formQuestionState.lockedFields || []).includes('answers')}
                          />
                        </InputRowContainer>
                        <InputRowContainer>
                          <LineAction
                            action={LineActionType.REMOVE}
                            onClick={() =>
                              handleRemoveAnswer(
                                answerIndex,
                                )
                              }
                            actionText={'Remove this answer'}
                            helpTextTitle={dRemoveAnswerTitle}
                            helpTextBody={dRemoveAnswerBody}
                            labelId={`${answerIndex}removeAnswer`}
                            disabled={readOnlyQuestion || !questionSupportOwnAnswers}
                          />
                        </InputRowContainer>
                      </>
                      ))
                    }

                    <InputRowContainer>
                      <LineAction
                        action={LineActionType.ADD}
                        onClick={() =>
                          handleAddAnswer()
                        }
                        actionText={'Add new answer'}
                        helpTextTitle={dAddAnswerTitle}
                        helpTextBody={dAddAnswerBody}
                        labelId={'Add new answer'}
                        disabled={readOnlyQuestion || !questionSupportOwnAnswers}
                      />
                    </InputRowContainer>


                    </Box>
                )}

                    {smallTabIndex === 2 && (
                    <Box>
                    {/* Scoring */}
                    <InputRowContainer>
                      <InputField
                        labelText={'Weight'}
                        labelId={'weight'}
                        mandatory={true}
                        helpTextTitle={sWeightTitle}
                        helpTextBody={sWeightBody}
                        value={formQuestionState.weight ?? 1}
                        onChange={(ivalue) => {
                          const value = ivalue.trim();
                          handleQuestionChange(
                            'weight',
                            value === '' ? 1 : Number(value)
                          );
                        }}
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            weight: true,
                          }))
                        }
                        fullWidth={false}
                        error={false}
                        helperText={''}
                        type={'number'}
                        disabled={!(scoringQuestions.includes(formQuestionState.questionType)) || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('weight') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                        displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('weight')}
                      />
                      <FBCheckbox
                        checked={formQuestionState.mustScore || false}
                        labelText={'Critical (must pass)'}
                        labelId={'mustPass'}
                        mandatory={false}
                        helpTextTitle={sMustPassTitle}
                        helpTextBody={sMustPassBody}
                        onChange={(checked) => {
                          handleQuestionChange(
                            'mustScore',
                            checked
                          );
                        }}
                        disabled={!(scoringQuestions.includes(formQuestionState.questionType)) || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('mustScore') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('mustScore')}
                      />
                    </InputRowContainer>
                    </Box>
                  )}

                  {smallTabIndex === 3 && (
                    <Box>
                      {/* Dependency */ }

                      <InputRowContainer>
                        <Box>
                          <FieldLock
                            onChange={function (locked: boolean): void {
                                handleLockedFields('externalDependency',locked);
                            } }
                            labelId={'externalDependency'}
                            disabled={locksDisabled}
                        displayed={locksDisplayed}
                            value={(formQuestionState.lockedFields || []).includes('externalDependency')}/>
                          {'External dependencies'}</Box>
                         </InputRowContainer>
                        { formQuestionState.externalDependency?.map(
                          (data, externalIndex) => (
                            <Box key={externalIndex} >
                              <InputRowContainer>
                                <SingleSelect
                                  labelText={'Logical operator'}
                                  labelId={'logicalOperator'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdLogicalOperatorTitle}
                                  helpTextBody={sdLogicalOperatorBody}
                                  onChange={(value) =>
                                    handleQuestionChange('externalDependency', value, props.language,externalIndex, 'logicalOperator')
                                  }
                                  options={logicalOperatorOptions}
                                  selectedValues={logicalOperatorOptions.filter(option => data.logicalOperator === option.value)}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('externalDependency')}
                                />
                                <SingleSelect
                                  labelText={'Variable'}
                                  labelId={'variable'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdVariableTitle}
                                  helpTextBody={sdVariableBody}
                                  onChange={(value) =>
                                    handleQuestionChange('externalDependency', value, props.language,externalIndex, 'variable')
                                  }
                                  options={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable')}
                                  selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable').filter(option => data.variable === option.value)}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('externalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                              <SingleSelect
                                  labelText={'Operator'}
                                  labelId={'operator'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdOperatorTitle}
                                  helpTextBody={sdOperatorBody}
                                  onChange={(value) =>
                                    handleQuestionChange('externalDependency', value, props.language,externalIndex, 'operator')
                                  }
                                  options={operatorOptions}
                                  selectedValues={operatorOptions.filter(option => data.operator === option.value)}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('externalDependency')}
                                />
                                <InputField
                                  labelText={'Compare value'}
                                  labelId={'compareValue'}
                                  mandatory={false}
                                  helpTextTitle={sdCompareValueTitle}
                                  helpTextBody={sdCompareValueBody}
                                  onChange={(value) =>
                                    handleQuestionChange('externalDependency', value, props.language,externalIndex, 'compareValue')
                                  }
                                  onBlur={() =>
                                    setTouchedFields((prev) => ({
                                      ...prev,
                                      externalDependency: true,
                                    }))
                                  }
                                  fullWidth={true}
                                  value={data.compareValue || ''}
                                  error={false}
                                  helperText={''}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('externalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                                <SingleSelect
                                  labelText={'Compare variable'}
                                  labelId={'compareVariable'}
                                  mandatory={false}
                                  fullWidth={true}
                                  helpTextTitle={sdCompareVariableTitle}
                                  helpTextBody={sdCompareVariableBody}
                                  onChange={(value) =>
                                    handleQuestionChange('externalDependency', value, props.language,externalIndex, 'compareVariable')
                                  }
                                  options={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable')}
                                  selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable').filter(option => data.compareVariable === option.value)}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('externalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                                <LineAction
                                  action={LineActionType.REMOVE}
                                  onClick={() =>
                                    handleRemoveDependency(
                                      true,
                                      externalIndex,
                                    )
                                  }
                                  actionText={'Remove this dependency'}
                                  helpTextTitle={sdRemoveDependencyTitle}
                                  helpTextBody={sdRemoveDependencyBody}
                                  labelId={`${externalIndex}Remove this dependency`}
                                  disabled={readOnlyQuestion}
                                />
                                </InputRowContainer>
                            </Box>
                        ))}
                        <InputRowContainer>
                          <LineAction
                            action={LineActionType.ADD}
                            onClick={() =>
                              handleAddDependency(true)
                            }
                            actionText={'Add new dependency'}
                            helpTextTitle={sdAddDependencyTitle}
                            helpTextBody={sdAddDependencyBody}
                            labelId={'Add new external dependency'}
                            disabled={readOnlyQuestion}
                          />
                        </InputRowContainer>

                        <InputRowContainer>
                        <Box>
                          <FieldLock
                            onChange={function (locked: boolean): void {
                                handleLockedFields('internalDependency',locked);
                            } }
                            labelId={'internalDependency'}
                            disabled={locksDisabled}
                        displayed={locksDisplayed}
                            value={(formQuestionState.lockedFields || []).includes('internalDependency')}/>
                          {'Internal dependencies'}</Box>
                         </InputRowContainer>
                        { formQuestionState.internalDependency?.map(
                          (data, internalIndex) => (
                            <Box key={internalIndex} >
                              <InputRowContainer>
                                <SingleSelect
                                  labelText={'Logical operator'}
                                  labelId={'logicalOperator'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdLogicalOperatorTitle}
                                  helpTextBody={sdLogicalOperatorBody}
                                  onChange={(value) =>
                                    handleQuestionChange('internalDependency', value, props.language,internalIndex, 'logicalOperator')
                                  }
                                  options={logicalOperatorOptions}
                                  selectedValues={logicalOperatorOptions.filter(option => data.logicalOperator === option.value)}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('internalDependency')}
                                />
                                <SingleSelect
                                  labelText={'Variable'}
                                  labelId={'variable'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdVariableTitle}
                                  helpTextBody={sdVariableBody}
                                  onChange={(value) =>
                                    handleQuestionChange('internalDependency', value, props.language,internalIndex, 'variable')
                                  }
                                  options={generatInternalVariableToOptions(props.formSections || [], 'internalVariable')}
                                  selectedValues={generatInternalVariableToOptions(props.formSections || [], 'internalVariable').filter(option => data.variable === option.value)}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('internalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                              <SingleSelect
                                  labelText={'Operator'}
                                  labelId={'operator'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdOperatorTitle}
                                  helpTextBody={sdOperatorBody}
                                  onChange={(value) =>
                                    handleQuestionChange('internalDependency', value, props.language,internalIndex, 'operator')
                                  }
                                  options={operatorOptions}
                                  selectedValues={operatorOptions.filter(option => data.operator === option.value)}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('internalDependency')}
                                />
                                <InputField
                                  labelText={'Compare value'}
                                  labelId={'compareValue'}
                                  mandatory={false}
                                  helpTextTitle={sdCompareValueTitle}
                                  helpTextBody={sdCompareValueBody}
                                  onChange={(value) =>
                                    handleQuestionChange('internalDependency', value, props.language,internalIndex, 'compareValue')
                                  }
                                  onBlur={() =>
                                    setTouchedFields((prev) => ({
                                      ...prev,
                                      internalDependency: true,
                                    }))
                                  }
                                  fullWidth={true}
                                  value={data.compareValue || ''}
                                  error={false}
                                  helperText={''}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('internalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                                <SingleSelect
                                  labelText={'Compare variable'}
                                  labelId={'compareVariable'}
                                  mandatory={false}
                                  fullWidth={true}
                                  helpTextTitle={sdCompareVariableTitle}
                                  helpTextBody={sdCompareVariableBody}
                                  onChange={(value) =>
                                    handleQuestionChange('internalDependency', value, props.language,internalIndex, 'compareVariable')
                                  }
                                  options={generatInternalVariableToOptions(props.formSections || [], 'internalVariable')}
                                  selectedValues={generatInternalVariableToOptions(props.formSections || [], 'internalVariable').filter(option => data.compareVariable === option.value)}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('internalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                                <LineAction
                                  action={LineActionType.REMOVE}
                                  onClick={() =>
                                    handleRemoveDependency(
                                      false,
                                      internalIndex,
                                    )
                                  }
                                  actionText={'Remove this dependency'}
                                  helpTextTitle={sdRemoveDependencyTitle}
                                  helpTextBody={sdRemoveDependencyBody}
                                  labelId={`${internalIndex}Remove this dependency`}
                                  disabled={readOnlyQuestion}
                                />
                                </InputRowContainer>
                            </Box>
                        ))}
                        <InputRowContainer>
                          <LineAction
                            action={LineActionType.ADD}
                            onClick={() =>
                              handleAddDependency(false)
                            }
                            actionText={'Add new dependency'}
                            helpTextTitle={sdAddDependencyTitle}
                            helpTextBody={sdAddDependencyBody}
                            labelId={'Add new external dependency'}
                            disabled={readOnlyQuestion}
                          />
                        </InputRowContainer>


                        <InputRowContainer>
                          <SingleSelect
                            labelText={'Alternative question type'}
                            labelId={'alternativeQuestionType'}
                            mandatory={false}
                            fullWidth={true}
                            helpTextTitle={dAlternativeQuestionTypeHelpTitle}
                            helpTextBody={dAlternativeQuestionTypeHelpBody}
                            onChange={(value) =>
                              handleQuestionChange(
                                'alternativeQuestionType',
                                value
                              )
                            }
                            options={questionTypeOptions}
                            selectedValues={questionTypeOptions.filter(option => formQuestionState.alternativeQuestionType === (option.value))}
                            disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('alternativeQuestionType') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                            isLocked={(formQuestionState.lockedFields || []).includes('alternativeQuestionType')}
                          />
                        </InputRowContainer>

                        { (formQuestionState.variableTranslations || []).length > 0 &&
                        <>
                          <p>Translations</p>
                          <LineSeparator/>
                        </>
                        }

                      <InputRowContainer>
                        <Box>
                          <FieldLock
                            onChange={function (locked: boolean): void {
                                handleLockedFields('variableTranslations',locked);
                            } }
                            labelId={'variableTranslations'}
                            disabled={locksDisabled}
                        displayed={locksDisplayed}
                            value={(formQuestionState.lockedFields || []).includes('variableTranslations')}/>
                          {'Variable translations'}</Box>
                         </InputRowContainer>
                { (formQuestionState.variableTranslations || []).map(
                  (data, translationIndex) => (
                    <>
                      <InputRowContainer>
                      <InputField
                        labelText={'Key'}
                        labelId={`${translationIndex}key}`}
                        mandatory={true}
                        helpTextTitle={dKeyTitle}
                        helpTextBody={dKeyBody}
                        value={data.key || ''}
                        onChange={(value) =>
                          handleQuestionChange(
                            'variableTranslations',
                            `K${value}`,
                            props.language,
                            translationIndex,
                          )
                        }
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            variableTranslations: true,
                          }))
                        }
                        fullWidth={false}
                        error={false}
                        helperText={''}
                        disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('variableTranslations') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('variableTranslations')}
                      />
                  <SingleSelect
                    labelText={'Type'}
                    labelId={`${translationIndex}type}`}
                    mandatory={true}
                    fullWidth={false}
                    helpTextTitle={dTypeTitle}
                    helpTextBody={dTypeBody}
                    onChange={(value) =>
                      handleQuestionChange(
                        'variableTranslations',
                        `T${value}`,
                        props.language,
                        translationIndex,
                      )
                    }
                    options={externalTypeOptions}
                    selectedValues={externalTypeOptions.filter(option => (data.type || 'string') === option.value)}
                    disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('variableTranslations') )}
                    onLock={handleLockedFields}
                    disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                    isLocked={(formQuestionState.lockedFields || []).includes('variableTranslations')}
                  />
                  <InputField
                    labelText={'Value'}
                    labelId={`${translationIndex}value}`}
                    mandatory={true}
                    helpTextTitle={dValueTitle}
                    helpTextBody={dValueBody}
                    value={ `${data.value}`}
                    onChange={(value) =>
                      handleQuestionChange(
                        'variableTranslations',
                        `V${value}`,
                        props.language,
                        translationIndex,
                      )
                    }
                    onBlur={() =>
                      setTouchedFields((prev) => ({
                        ...prev,
                        variableTranslations: true,
                      }))
                    }
                    fullWidth={false}
                    error={false}
                    helperText={''}
                    disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('variableTranslations') )}
                    onLock={handleLockedFields}
                    disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                    isLocked={(formQuestionState.lockedFields || []).includes('variableTranslations')}
                  />
                  </InputRowContainer>
                  <InputRowContainer>
                  <LineAction
                    action={LineActionType.REMOVE}
                    onClick={() =>
                      handleRemoveTranslationData(
                        translationIndex,
                        )
                      }
                    actionText={'Remove this translation'}
                    helpTextTitle={dRemoveTranslationTitle}
                    helpTextBody={dRemoveTranslationBody}
                    labelId={`${translationIndex}removeTranslation`}
                    disabled={readOnlyQuestion}
                  />
                  </InputRowContainer>
                  </>
                    ))
                    }
                    <InputRowContainer>
                    <LineAction
                      action={LineActionType.ADD}
                      onClick={() =>
                        handleAddTranslation()
                      }
                      actionText={'Add new translation'}
                      helpTextTitle={dAddTitle}
                      helpTextBody={dAddBody}
                      labelId={'Add new translation'}
                      disabled={readOnlyQuestion}
                    />
                    </InputRowContainer>
                  </Box>
                      )}

                  {smallTabIndex === 4 && (
                    <Box>
                    {/* references */}
                    <InputRowContainer>
                        <ReferenceImage
                          readOnly={readOnlyQuestion}
                          selectedImages={referenceImageList}
                          onAddImage={handleOnAddImage}
                          onRemoveImage={handleOnRemoveImage}

                          disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('references') )}
                          onLock={handleLockedFields}
                          disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                          isLocked={(formQuestionState.lockedFields || []).includes('references')}
                        />
                      </InputRowContainer>
                    </Box>
                  )}

                  {smallTabIndex === 5 && (
                    <Box>
                    {/* Advanced */}

                    {/* Number table */}
                    <ConditionalWrapper show={formQuestionState.questionType === FbeQuestionType.NUMBER_TABLE}>
                      <InputRowContainer>
                        <InputField
                          labelText={'Table row name'}
                          labelId={'rowShort'}
                          mandatory={false}
                          helpTextTitle={giRowShortTextHelpTitle}
                          helpTextBody={giRowShortTextHelpBody}
                          onChange={(value) =>
                            handleQuestionChange('rowShort', value)
                          }
                          onBlur={() =>
                            setTouchedFields((prev) => ({
                              ...prev,
                              rowShort: true,
                            }))
                          }
                          fullWidth={true}
                          value={formQuestionState.rowShort?.[0]?.text || ''}
                          error={false}
                          helperText={''}
                          disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('rowShort') )}
                          onLock={handleLockedFields}
                          disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                          isLocked={(formQuestionState.lockedFields || []).includes('rowShort')}
                        />
                        <InputField
                          labelText={'Table column name'}
                          labelId={'columnShort'}
                          mandatory={false}
                          helpTextTitle={giColumnShortTextHelpTitle}
                          helpTextBody={giColumnShortTextHelpBody}
                          onChange={(value) =>
                            handleQuestionChange('columnShort', value)
                          }
                          onBlur={() =>
                            setTouchedFields((prev) => ({
                              ...prev,
                              columnShort: true,
                            }))
                          }
                          fullWidth={true}
                          value={formQuestionState.columnShort?.[0]?.text || ''}
                          error={false}
                          helperText={''}
                          disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('columnShort') )}
                          onLock={handleLockedFields}
                          disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                          isLocked={(formQuestionState.lockedFields || []).includes('columnShort')}
                        />
                      </InputRowContainer>
                      <InputRowContainer>
                        <SingleSelect
                            labelText={'Table type'}
                            labelId={'tableType'}
                            mandatory={true}
                            fullWidth={true}
                            helpTextTitle={aTableTypeHelpTitle}
                            helpTextBody={aTableTypeHelpBody}
                            onChange={(value) =>
                              handleQuestionChange('tableType', value)
                            }
                            options={tableTypeOptions}
                            selectedValues={tableTypeOptions.filter(option => formQuestionState.tableType === option.value)}
                            disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('tableType') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                            isLocked={(formQuestionState.lockedFields || []).includes('tableType')}
                          />
                        <FBRadio
                          row={true}
                          labelId={'ascendingOrientation'}
                          value={
                            (formQuestionState.ascendingOrientation || true) === true
                              ? 'ascending'
                              : 'descending'
                          }
                          onChange={(value) =>
                            handleQuestionChange('ascendingOrientation', value)
                          }
                          labelText={'Table orientation'}
                          mandatory={true}
                          helpTextTitle={giOrientationHelpTitle}
                          helpTextBody={giOrientationHelpBody}
                          valueList={[
                            {
                              helpTitle:`${giOrientationHelpTitle}`,
                              helpBody:`${giOrientationHelpBody}`,
                              value: 'ascending',
                              label:'Ascending',
                              mandatory: true
                            },
                            {
                              helpTitle:`${giOrientationHelpTitle}`,
                              helpBody:`${giOrientationHelpBody}`,
                              value: 'descending',
                              label:'Descending',
                              mandatory: true
                            },
                          ]}
                          disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('ascendingOrientation') )}
                          onLock={handleLockedFields}
                          disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                          isLocked={(formQuestionState.lockedFields || []).includes('ascendingOrientation')}
                        />
                        </InputRowContainer>
                        <InputRowContainer>
                          <SingleSelect
                            labelText={'Table orientation internal variable'}
                            labelId={'ascendingOrientationInternalVariable'}
                            mandatory={false}
                            fullWidth={true}
                            helpTextTitle={giOrientationInternalVariableHelpTitle}
                            helpTextBody={giOrientationInternalVariableHelpBody}
                            onChange={(value) =>
                              handleQuestionChange('ascendingOrientationInternalVariable', value)
                            }
                            options={generatInternalVariableToOptions(props.formSections || [], 'ascendingOrientationInternalVariable')}
                            selectedValues={generatInternalVariableToOptions(props.formSections || [], 'ascendingOrientationInternalVariable').filter(option => formQuestionState.ascendingOrientationInternalVariable === option.value)}
                            disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('ascendingOrientationInternalVariable') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                            isLocked={(formQuestionState.lockedFields || []).includes('ascendingOrientationInternalVariable')}
                          />
                          <InputField
                            labelText={'Table orientation internal value'}
                            labelId={'ascendingOrientationInternalValue'}
                            mandatory={false}
                            helpTextTitle={giOrientationInternalValueHelpTitle}
                            helpTextBody={giOrientationInternalValueHelpBody}
                            onBlur={(value) => void value
                            }
                            onChange={(value) =>
                              handleQuestionChange('ascendingOrientationInternalValue', value)
                            }
                            fullWidth={true}
                            value={formQuestionState.ascendingOrientationInternalValue || ''}
                            error={false}
                            helperText={''}
                            disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('ascendingOrientationInternalValue') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                            isLocked={(formQuestionState.lockedFields || []).includes('ascendingOrientationInternalValue')}
                          />
                        </InputRowContainer>
                        <InputRowContainer>
                          {'Row position names'}
                         </InputRowContainer>
                        { formQuestionState.rowPositionNames?.map(
                          (data, externalIndex) => (
                            <Box key={externalIndex} >
                              <InputRowContainer>
                              <InputField
                                  labelText={'Position'}
                                  labelId={'position'}
                                  mandatory={true}
                                  helpTextTitle={aRowPositionTitle}
                                  helpTextBody={aRowPositionBody}
                                  onChange={(value) =>
                                    handleQuestionChange('rowPositionNames', value, props.language,externalIndex, 'position')
                                  }
                                  onBlur={() =>
                                    setTouchedFields((prev) => ({
                                      ...prev,
                                      rowPositionNames: true,
                                    }))
                                  }
                                  fullWidth={true}
                                  value={data.position || ''}
                                  error={false}
                                  helperText={''}
                                  disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('rowPositionNames') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                  isLocked={(formQuestionState.lockedFields || []).includes('rowPositionNames')}
                                />
                                <InputField
                                    labelText={'Name'}
                                    labelId={'name'}
                                    mandatory={true}
                                    helpTextTitle={aRowPositionNameTitle}
                                    helpTextBody={aRowPositionNameBody}
                                    onChange={(value) =>
                                      handleQuestionChange('rowPositionNames', value, props.language,externalIndex, 'name')
                                    }
                                    onBlur={() =>
                                      setTouchedFields((prev) => ({
                                        ...prev,
                                        rowPositionNames: true,
                                      }))
                                    }
                                    fullWidth={true}
                                    value={data.name && data.name.length> 0 ? data.name[0].text : ''}
                                    error={false}
                                    helperText={''}
                                    disabled={readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('rowPositionNames') )}
                                    onLock={handleLockedFields}
                                    disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                                    isLocked={(formQuestionState.lockedFields || []).includes('rowPositionNames')}
                                  />
                              </InputRowContainer>
                              <InputRowContainer>
                                <LineAction
                                  action={LineActionType.REMOVE}
                                  onClick={() =>
                                    handleRemoveRowPosition(
                                      externalIndex,
                                    )
                                  }
                                  actionText={'Remove this postion and name'}
                                  helpTextTitle={aRemoveRowPositionNameTitle}
                                  helpTextBody={aRemoveRowPositionNameBody}
                                  labelId={`${externalIndex}Remove this position and name`}
                                  disabled={readOnlyQuestion}
                                />
                                </InputRowContainer>
                            </Box>
                        ))}
                        <InputRowContainer>
                          <LineAction
                            action={LineActionType.ADD}
                            onClick={() =>
                              handleAddRowPosition()
                            }
                            actionText={'Add new position and name'}
                            helpTextTitle={aAddRowPositionTitle}
                            helpTextBody={aAddRowPositionBody}
                            labelId={'Add new position and name'}
                            disabled={readOnlyQuestion}
                          />
                        </InputRowContainer>

                    </ConditionalWrapper>

                    {/* Number */}
                    <ConditionalWrapper show={formQuestionState.questionType === FbeQuestionType.NUMBER}>
                    <InputRowContainer>
                      <InputField
                        labelText={'Minimum allowed value'}
                        labelId={'minAllowedValue'}
                        mandatory={false}
                        helpTextTitle={nlMinValueTitle}
                        helpTextBody={nlMinValueBody}
                        value={formQuestionState.minAllowedValue || ''}
                        onChange={(value) => {
                          handleQuestionChange('minAllowedValue', value)
                        }}
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            minAllowedValue: true,
                          }))
                        }
                        fullWidth={false}
                        error={false}
                        helperText={''}
                        type={'number'}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('minAllowedValue') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('minAllowedValue')}
                      />
                      <InputField
                        labelText={'Maximum allowed value'}
                        labelId={'maxAllowedValue'}
                        mandatory={false}
                        helpTextTitle={nlMaxValueTitle}
                        helpTextBody={nlMaxValueBody}
                        value={formQuestionState.maxAllowedValue || ''}
                        onChange={(value) => {
                          handleQuestionChange('maxAllowedValue', value)
                        }}
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            maxAllowedValue: true,
                          }))
                        }
                        fullWidth={false}
                        error={false}
                        helperText={''}
                        type={'number'}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('maxAllowedValue') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('maxAllowedValue')}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                      <SingleSelect
                        labelText={'Minimum external variable'}
                        labelId={'minAllowedExternalVariable'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMinExternalVariableTitle}
                        helpTextBody={nlMinExternalVariableBody}
                        onChange={(value) =>
                          handleQuestionChange('minAllowedExternalVariable', value)
                        }
                        options={generateExternalDataToOptions(props.form?.externalData || [], 'minAllowedExternalVariable')}
                        selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'minAllowedExternalVariable').filter(option => formQuestionState.minAllowedExternalVariable === option.value)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('minAllowedExternalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('minAllowedExternalVariable')}
                      />
                      <SingleSelect
                        labelText={'Maximum external variable'}
                        labelId={'maxAllowedExternalVariable'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMaxExternalVariableTitle}
                        helpTextBody={nlMaxExternalVariableBody}
                        onChange={(value) =>
                          handleQuestionChange('maxAllowedExternalVariable', value)
                        }
                        options={generateExternalDataToOptions(props.form?.externalData || [], 'maxAllowedExternalVariable')}
                        selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'maxAllowedExternalVariable').filter(option => formQuestionState.maxAllowedExternalVariable === option.value)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('maxAllowedExternalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('maxAllowedExternalVariable')}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                      <SingleSelect
                        labelText={'Minimum internal variable'}
                        labelId={'minAllowedInternalVariable'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMinInternalVariableTitle}
                        helpTextBody={nlMinInternalVariableBody}
                        onChange={(value) =>
                          handleQuestionChange('minAllowedInternalVariable', value)
                        }
                        options={generatInternalVariableToOptions(props.formSections || [], 'minAllowedInternalVariable')}
                        selectedValues={generatInternalVariableToOptions(props.formSections || [], 'minAllowedInternalVariable').filter(option => formQuestionState.minAllowedInternalVariable === option.value)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('minAllowedInternalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('minAllowedInternalVariable')}
                      />
                      <SingleSelect
                        labelText={'Maximum internal variable'}
                        labelId={'maxAllowedInternalVariable'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMaxInternalVariableTitle}
                        helpTextBody={nlMaxInternalVariableBody}
                        onChange={(value) =>
                          handleQuestionChange('maxAllowedInternalVariable', value)
                        }
                        options={generatInternalVariableToOptions(props.formSections || [], 'maxAllowedInternalVariable')}
                        selectedValues={generatInternalVariableToOptions(props.formSections || [], 'maxAllowedInternalVariable').filter(option => formQuestionState.maxAllowedInternalVariable === option.value)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('maxAllowedInternalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('maxAllowedInternalVariable')}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                      <InputField
                        labelText={'Negative tolerance value'}
                        labelId={'toleranceMinValue'}
                        mandatory={false}
                        helpTextTitle={nlMinTolValueTitle}
                        helpTextBody={nlMinTolValueBody}
                        value={formQuestionState.toleranceMinValue || ''}
                        onChange={(value) => {
                          handleQuestionChange('toleranceMinValue', value)
                        }}
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            toleranceMinValue: true,
                          }))
                        }
                        fullWidth={false}
                        error={false}
                        helperText={''}
                        type={'number'}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('toleranceMinValue') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('toleranceMinValue')}
                      />
                      <InputField
                        labelText={'Positive tolerance value'}
                        labelId={'tolerancePlusValue'}
                        mandatory={false}
                        helpTextTitle={nlMaxTolValueTitle}
                        helpTextBody={nlMaxTolValueBody}
                        value={formQuestionState.tolerancePlusValue || ''}
                        onChange={(value) => {
                          handleQuestionChange('tolerancePlusValue', value)
                        }}
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            tolerancePlusValue: true,
                          }))
                        }
                        fullWidth={false}
                        error={false}
                        helperText={''}
                        type={'number'}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('tolerancePlusValue') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('tolerancePlusValue')}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                      <SingleSelect
                        labelText={'Negative tolerance external variable'}
                        labelId={'toleranceMinExternalVariable'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMinTolExternalVariableTitle}
                        helpTextBody={nlMinTolExternalVariableBody}
                        onChange={(value) =>
                          handleQuestionChange('toleranceMinExternalVariable', value)
                        }
                        options={generateExternalDataToOptions(props.form?.externalData || [], 'toleranceMinExternalVariable')}
                        selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'toleranceMinExternalVariable').filter(option => formQuestionState.toleranceMinExternalVariable === option.value)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('toleranceMinExternalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('toleranceMinExternalVariable')}
                      />
                      <SingleSelect
                        labelText={'Positive tolerance external variable'}
                        labelId={'tolerancePlusExternalVariable'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMaxTolExternalVariableTitle}
                        helpTextBody={nlMaxTolExternalVariableBody}
                        onChange={(value) =>
                          handleQuestionChange('tolerancePlusExternalVariable', value)
                        }
                        options={generateExternalDataToOptions(props.form?.externalData || [], 'tolerancePlusExternalVariable')}
                        selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'tolerancePlusExternalVariable').filter(option => formQuestionState.tolerancePlusExternalVariable === option.value)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('tolerancePlusExternalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('tolerancePlusExternalVariable')}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                      <SingleSelect
                        labelText={'Negative tolerance internal variable'}
                        labelId={'toleranceMinInternalVariable'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMinTolInternalVariableTitle}
                        helpTextBody={nlMinTolInternalVariableBody}
                        onChange={(value) =>
                          handleQuestionChange('toleranceMinInternalVariable', value)
                        }
                        options={generatInternalVariableToOptions(props.formSections || [], 'toleranceMinInternalVariable')}
                        selectedValues={generatInternalVariableToOptions(props.formSections || [], 'toleranceMinInternalVariable').filter(option => formQuestionState.toleranceMinInternalVariable === option.value)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('toleranceMinInternalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('toleranceMinInternalVariable')}
                      />
                      <SingleSelect
                        labelText={'Positive tolerance internal variable'}
                        labelId={'tolerancePlusInternalVariable'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMaxTolInternalVariableTitle}
                        helpTextBody={nlMaxTolInternalVariableBody}
                        onChange={(value) =>
                          handleQuestionChange('tolerancePlusInternalVariable', value)
                        }
                        options={generatInternalVariableToOptions(props.formSections || [], 'tolerancePlusInternalVariable')}
                        selectedValues={generatInternalVariableToOptions(props.formSections || [], 'tolerancePlusInternalVariable').filter(option => formQuestionState.tolerancePlusInternalVariable === option.value)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('tolerancePlusInternalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('tolerancePlusInternalVariable')}
                      />
                    </InputRowContainer>

                    <InputRowContainer>
                      <SingleSelect
                        labelText={'Adjustment direction'}
                        labelId={'multiAdjustmentDirection'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMultiAdjustmentDirectionTitle}
                        helpTextBody={nlMultiAdjustmentDirectionBody}
                        onChange={(value) =>
                          handleQuestionChange('multiAdjustmentDirection', value)
                        }
                        options={multiAdjustmentDirectionOptions}
                        selectedValues={multiAdjustmentDirectionOptions.filter(option => formQuestionState.multiAdjustmentDirection === option.value as FbeNumberMultiAdjustmentDirectionType)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('multiAdjustmentDirection') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('multiAdjustmentDirection')}
                      />
                      <SingleSelect
                        labelText={'Adjustment impact'}
                        labelId={'multiAdjustmentImpact'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlMultiAdjustmentImpactTitle}
                        helpTextBody={nlMultiAdjustmentImpactBody}
                        onChange={(value) =>
                          handleQuestionChange('multiAdjustmentImpact', value)
                        }
                        options={multiAdjustmentImpactOptions}
                        selectedValues={multiAdjustmentImpactOptions.filter(option => formQuestionState.multiAdjustmentImpact === option.value as FbeNumberMultiAdjustmentImpactType)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('multiAdjustmentImpact') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('multiAdjustmentImpact')}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                    <SingleSelect
                        labelText={'Mean internal variable'}
                        labelId={'actualValueInternalVariable'}
                        mandatory={false}
                        fullWidth={false}
                        helpTextTitle={nlActualValueInternalVariableTitle}
                        helpTextBody={nlActualValueInternalVariableBody}
                        onChange={(value) =>
                          handleQuestionChange('actualValueInternalVariable', value)
                        }
                        options={generatInternalVariableToOptions(props.formSections || [], 'actualValueInternalVariable')}
                        selectedValues={generatInternalVariableToOptions(props.formSections || [], 'actualValueInternalVariable').filter(option => formQuestionState.actualValueInternalVariable === option.value)}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('actualValueInternalVariable') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('actualValueInternalVariable')}
                      />
                    <InputField
                        labelText={'Mean'}
                        labelId={'actualValue'}
                        mandatory={false}
                        helpTextTitle={nlActualValueTitle}
                        helpTextBody={nlActualValueBody}
                        value={formQuestionState.actualValue || ''}
                        onChange={(value) =>
                          handleQuestionChange('actualValue', value)
                        }
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            actualValue: true,
                          }))
                        }
                        fullWidth={false}
                        error={false}
                        helperText={''}
                        type={'number'}
                        disabled={formQuestionState.questionType !== FbeQuestionType.NUMBER || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('actualValue') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('actualValue')}
                      />
                    </InputRowContainer>
                    </ConditionalWrapper>

                    {/* string */}
                    <ConditionalWrapper show={formQuestionState.questionType === FbeQuestionType.TEXT}>
                    <InputRowContainer>
                      <InputField
                        labelText={'Pattern'}
                        labelId={'pattern'}
                        mandatory={false}
                        helpTextTitle={nlPatternTitle}
                        helpTextBody={nlPatternBody}
                        value={formQuestionState.pattern || ''}
                        onChange={(value) => {
                          handleQuestionChange('pattern', value)
                        }}
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            pattern: true,
                          }))
                        }
                        fullWidth={false}
                        error={false}
                        helperText={''}
                        disabled={formQuestionState.questionType !== FbeQuestionType.TEXT || readOnlyQuestion || (locksDisabled && (formQuestionState.lockedFields || []).includes('pattern') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formQuestionState.lockedFields || []).includes('pattern')}
                      />
                    </InputRowContainer>
                    </ConditionalWrapper>

                    </Box>
                  )}
                </EditContainer>
                <GapStyled
                  mt={3}
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="flex-end"
                >
                  <ButtonStyled
                    variant="outlined"
                    onClick={() => {
                      history('/');
                    }}
                  >
                    Cancel
                  </ButtonStyled>
                  <ButtonStyled
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      props.onSubmitAllSections();
                    }}
                    disabled={
                      readOnlyQuestion ||
                      !props.form ||
                      !props.form.pk ||
                      props.form.pk === '' ||
                      !formQuestionValid ||
                      !changesMade}
                  >
                    Save
                  </ButtonStyled>
                </GapStyled>
                </Box>
                </>
            )}
          </Box>
        </Grid2>
    </>
  );
}
